import { computed, reactive, toRefs, nextTick, unref } from 'vue'
import { clone } from '@/common/js/just.js'
import { MessageBox } from 'element-ui'
import { Message } from 'view-design'
import { getTagsList, getTag, deleteTag, addTag, aiTagStateToggle, saveTag, batchUpdateTag } from '@/api/manage.model'
import { getSysTagByTree } from '@/api/common.model.js'
import { refreshPageData, SYS_TAGS } from '@/data/global-opts'
import { isArray } from '@/common/js/utils'
import { FORM_TYPE, LABEL_TYPES } from '../const.js'
import moment from 'moment'

// 递归树形表格数据所有层级，去掉hasChildren字段，不然会有显示问题
export const tagTreeLoop = (arr) => {
  if (!arr || !Array.isArray(arr)) return []
  arr.forEach((i) => {
    const upTimeText = i.upTime ? moment(i.upTime).format('YYYY-MM-DD HH:mm:ss') : ''
    const createTimeText = i.createTime ? moment(i.createTime).format('YYYY-MM-DD HH:mm:ss') : ''

    i.createTimeText = createTimeText
    i.upTimeText = upTimeText
    delete i.hasChildren
    if (i.children?.length) {
      tagTreeLoop(i.children)
    }
  })
  return arr
}

// 根据查询条件，递归过滤树形表格数据实现前端查询（交集过滤）
const tagTreeLoopFilter = (arr, query = {}) => {
  // 清理空值查询条件
  for (const i in query) {
    if (query[i] === '' || query[i] === null || query[i] === undefined) {
      delete query[i]
    }
  }

  // labelId 为素材样式标签树形结构本地筛选一级素材样式的字段
  // aiEnable、belongType为ai标签树形结构本地筛选的字段
  const { keyWord, upOpId, labelId, aiEnable, belongType, gameGroupId, labelType } = query
  const isImageStyle = labelType === LABEL_TYPES.MATERIAL_STYLE_LABEL
  const isAiTag = typeof labelType === 'undefined'
  const queryCount = Object.keys(query).length

  // 如果没有查询条件，返回原数组
  if (queryCount === 0) {
    return arr
  }

  const filterNode = (node) => {
    let matches = true // 默认匹配，所有条件都必须满足

    // 关键词搜索（labelId 或 content 匹配其一即可）
    if (keyWord) {
      const queryText = keyWord.toLowerCase()
      const labelIdMatch = String(node.labelId).toLowerCase().includes(queryText)
      const contentMatch = node.content?.toLowerCase().includes(queryText)

      // 关键词条件：labelId 或 content 匹配其一即可
      if (!labelIdMatch && !contentMatch) {
        matches = false
      }
    }
    // 游戏组id精确匹配
    if (gameGroupId !== undefined && matches) {
      const gameGroupIds = node.gameGroupIds?.split(',')?.map(Number) || []
      if (!gameGroupIds.includes(gameGroupId)) {
        matches = false
      }
    }

    // 用户ID精确匹配
    if (upOpId !== undefined && matches) {
      if (upOpId !== node.upOpId) {
        matches = false
      }
    }

    // AI启用状态精确匹配
    if (aiEnable !== undefined && matches) {
      if (aiEnable !== node.aiEnable) {
        matches = false
      }
    }

    // 归属类型精确匹配
    if (typeof belongType === 'number' && matches) {
      if (belongType !== node.belongType) {
        matches = false
      }
    }

    // 一级素材样式筛选
    if (isImageStyle) {
      if (labelId) {
        // 素材样式：一级素材样式筛选
        const labelIdMatch = String(node.labelId).toLowerCase().includes(labelId)
        if (!labelIdMatch) {
          matches = false
        }

        // 如果存在一级素材样式筛选，这里就做返回了，不再遍历子节点
        return matches
      }
    }

    if (node.children?.length) {
      node.children = node.children.filter(filterNode)
      // 如果当前节点不匹配但有匹配的子节点，保留该节点
      if (!matches && node.children.length > 0) {
        matches = true
      }
    }

    return matches
  }
  return arr.filter(filterNode)
}

// 递归删除树形结构中最后一级的子节点
const tagTreeLoopRemoveLastChildren = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return arr
  }

  const processNode = (node) => {
    // 如果当前节点有子节点
    if (node.children && Array.isArray(node.children) && node.children.length > 0) {
      // 检查子节点是否还有更深层的子节点
      const hasGrandChildren = node.children.some(child =>
        child.children && Array.isArray(child.children) && child.children.length > 0
      )

      if (hasGrandChildren) {
        // 如果有孙子节点，递归处理子节点
        node.children = node.children.map(processNode)
      } else {
        // 如果没有孙子节点，说明子节点是最后一级，删除它们
        delete node.children
        // 或者设置为空数组: node.children = []
      }
    }

    return node
  }

  return arr.map(processNode)
}

export const useTableView = () => {
  const state = reactive({
    displayView: true
  })

  const viewType = computed({
    get: () => {
      return {
        table: state.displayView,
        tree: !state.displayView
      }
    },
    set: (val) => {
      state.displayView = val
    }
  })

  const onToggleViewType = () => {
    viewType.value = !state.displayView
  }

  return {
    viewType,
    onToggleViewType
  }
}

export const useTable = (queryFormRef, labelType, viewType = {}) => {
  let originTagTreeData = []
  const tableDataModel = reactive({
    tagTreeTableRef: null,
    tableHeight: 330,
    tableData: [],
    tagTreeData: [],
    parentTags: [],
    tableSortParams: { sort: '', order: '' },
    // displayView: true,
    selectionList: [],
    tableLoading: false,
    pager: {
      pageId: 1,
      pageSize: 30,
      total: 0
    }
  })

  const onQueryAction = () => {
    if (viewType.value?.tree) {
      onLocalTreeDataFilter()
      return
    }
    tableDataModel.pager.pageId = 1
    getTableData()
  }

  const getTreeData = () => {
    getSysTagByTree({ labelType: unref(labelType) }).then((data) => {
      const list = tagTreeLoop(data)
      originTagTreeData = clone(list)
      tableDataModel.parentTags = tagTreeLoopRemoveLastChildren(clone(list))
      tableDataModel.tagTreeData = list

      // 每次重新获取树形数据后，进行一次根据筛选条件的本地全局过滤
      onLocalTreeDataFilter()
    })
  }

  const onToggleRowExpansion = (expand) => {
    tableDataModel.tagTreeData.forEach((i) => {
      tableDataModel.tagTreeTableRef.toggleRowExpansion(i, expand)
    })
  }

  const getTableData = () => {
    const queryForm = queryFormRef.value.getForm()
    const { pageSize, pageId } = tableDataModel.pager
    const { order, sort } = tableDataModel.tableSortParams || {}
    tableDataModel.tableLoading = true
    const queryParams = { ...queryForm, labelType: unref(labelType), pageId, pageSize }
    if (sort) queryParams.sort = sort
    if (order) queryParams.order = order

    getTagsList(queryParams).then((data) => {
      const { list, page_info } = data
      const { total_number } = page_info || {}

      if (isArray(list)) {
        tableDataModel.tableData = list
        tableDataModel.pager.total = total_number
      }
    }).finally(() => {
      tableDataModel.tableLoading = false
    })
  }

  const onTableSelectionChange = (selection) => {
    tableDataModel.selectionList = selection
  }

  const onTableRowRemove = (row) => {
    MessageBox.confirm('此操作将永久删除该标签, 是否继续?', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(() => {
      const { labelId } = row
      deleteTag({ labelId }).then((data) => {
        if (data) {
          getTableData()
          getTreeData()
          refreshPageData([SYS_TAGS])
        }
      })
    })
  }

  const onTableRowStateToggle = ({ labelId, aiEnable, content }) => {
    MessageBox.confirm(`此操作将${aiEnable ? '停用' : '启用'}[${content}]标签, 是否继续?`, '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(() => {
      aiTagStateToggle({ labelId, aiEnable: aiEnable ? 0 : 1 }).then((data) => {
        if (data) {
          getTableData()
          getTreeData()
        }
      })
    })
  }

  async function onLocalTreeDataFilter() {
    const queryForm = queryFormRef.value.getForm()
    const nodes = tagTreeLoopFilter(clone(originTagTreeData), { ...queryForm, labelType: labelType?.value })
    tableDataModel.tagTreeData = nodes
  }

  return {
    ...toRefs(tableDataModel),
    onQueryAction,
    getTreeData,
    getTableData,
    onToggleRowExpansion,
    onTableRowRemove,
    onTableRowStateToggle,
    onTableSelectionChange
  }
}

export const useAddUpdateModal = ({ getTableData, getTreeData, labelType }) => {
  const formModel = reactive({
    showTagFormModal: false,
    tagFormRef: null,
    onConfirmLoading: false,
    modalType: FORM_TYPE.ADD
  })

  const onShowAddTagModal = () => {
    formModel.showTagFormModal = true
  }

  const onBatchModify = (selectionList) => {
    if (!selectionList?.length) {
      return Message.warning('请选择要修改的素材样式')
    }

    formModel.modalType = FORM_TYPE.BATCH
    formModel.showTagFormModal = true
  }

  const onCloseModal = () => {
    formModel.showTagFormModal = false
    formModel.modalType = FORM_TYPE.ADD
    formModel.tagFormRef.resetForm()
  }

  const onTagFormConfirm = (selectionList) => {
    formModel.tagFormRef.validate().then(() => {
      const form = formModel.tagFormRef.getForm()
      formModel.onConfirmLoading = true
      if (formModel.modalType === FORM_TYPE.BATCH) {
        batchUpdateTags(form, selectionList)
      } else {
        form.labelId ? updateTag(form) : addNewTag(form)
      }
    }).catch((err) => {
      console.log('err output: ', err)
    })
  }

  function batchUpdateTags(form, selectionList) {
    const { gameIds } = form
    batchUpdateTag({ labelIds: selectionList.map(i => i.labelId), gameIds: gameIds.join(',') }).then((data) => {
      if (data) {
        const { successTotal, failTotal, failReason } = data
        if (typeof successTotal === 'number' || typeof failTotal === 'number') {
          MessageBox.alert(`成功${successTotal}条，失败${failTotal}条${failReason ? `，${failReason}` : ''}`, {
            title: '批量添加结果',
            confirmButtonText: '知道了',
            dangerouslyUseHTMLString: true
          })
        }
        getTableData()
        getTreeData && getTreeData()
        onCloseModal()
      }
    }).finally(() => {
      formModel.onConfirmLoading = false
    })
  }

  function addNewTag(form) {
    let { labels, gameIds, parentId, level, labelDesc, aiEnable } = form
    labels = labels.split('\n').map(txt => txt.trim()).filter(txt => txt !== '')
    const params = { labels, aiEnable, gameIds: gameIds.join(','), labelType: unref(labelType), labelDesc }

    if (unref(labelType) === LABEL_TYPES.MATERIAL_STYLE_LABEL) {
      if (parentId) {
        params.parentId = parentId
      } else {
        // 如果新增时，素材样式没有选择一级样式，则不绑定游戏
        params.gameIds = undefined
      }
    }
    if (unref(labelType) === LABEL_TYPES.AI_LABEL) {
      if (parentId) {
        params.parentId = parentId
      }
    }

    addTag(params).then((data) => {
      if (data) {
        const { successTotal, failTotal, failReason } = data
        if (typeof successTotal === 'number' || typeof failTotal === 'number') {
          MessageBox.alert(`成功${successTotal}条，失败${failTotal}条${failReason ? `，${failReason}` : ''}`, {
            title: '批量添加结果',
            confirmButtonText: '知道了',
            dangerouslyUseHTMLString: true
          })
        }
        getTableData()
        getTreeData && getTreeData()
        onCloseModal()
        refreshPageData([SYS_TAGS])
      }
    }).finally(() => {
      formModel.onConfirmLoading = false
    })
  }

  function updateTag(form) {
    const { label, labelId, gameIds, labelDesc, parentId, aiEnable } = form
    const params = {
      gameIds: gameIds.join(','), content: label.trim(), aiEnable, labelId, labelDesc
    }

    // 目前素材样式、ai标签 可以更改父级
    if (parentId) {
      params.parentId = parentId
    }

    saveTag(params).then((data) => {
      if (data) {
        getTableData()
        getTreeData && getTreeData()
        onCloseModal()
      }
    }).finally(() => {
      formModel.onConfirmLoading = false
    })
  }

  return {
    ...toRefs(formModel),
    onShowAddTagModal,
    onBatchModify,
    onTagFormConfirm,
    onCloseModal
  }
}
